import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { StandardEvents } from '@shopify/events';
import { debounce } from '@theme/utilities';
import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

/** How long to coalesce cart activity into a single recommendations refresh.
 *
 * One customer action on a pack line is not one cart event: a tier swap removes lines, adds
 * lines, reconciles and then fires a corrective update, so this listener used to fire 2-4 times
 * per click and each one cleared the cache and refetched. Measured on a throttled mobile
 * profile: five `/recommendations/products` requests for a single tap, two of them in the same
 * millisecond — all competing for bandwidth with the cart's own repaint, which is what the
 * shopper is actually waiting on. Longer than the cart's own 300 ms debounce so the refresh
 * lands after the cart has settled rather than alongside it. */
const RECOMMENDATIONS_REFRESH_DEBOUNCE_MS = 400;

/**
 * Esperas entre reintentos, y su cantidad: una entrada por reintento.
 *
 * REPORTADO: los recomendados "no siempre cargan", en los tres lugares donde vive este
 * componente (cajón del carrito, cajón de menú, y debajo de las reseñas en la ficha).
 *
 * El endpoint no es el problema: medido contra el sitio en vivo responde 200 con contenido, de
 * forma estable, en los dos `intent`. El problema era que acá **cualquier** fallo se trataba
 * como definitivo — `#handleError` escondía la sección y el `MutationObserver` de abajo ignora
 * a propósito los dos cambios que hace (`hidden` y `data-error`) para no entrar en bucle, así
 * que no quedaba ningún camino de vuelta. Un microcorte de red, un 5xx o un `morphSection` que
 * pierde la carrera contra otro re-render del carrito y los recomendados desaparecían hasta
 * recargar la página entera.
 *
 * Lo de `morphSection` no es hipotético: el comentario de `#loadRecommendations` ya documenta
 * que la sección puede faltar transitoriamente si otro render del carrito cae en el medio.
 * Pasarlo a `await` arregló que bloqueara los refrescos futuros, pero mandó ese throw derecho
 * al `catch` — o sea que cambió "queda desactualizado" por "desaparece para siempre".
 *
 * Total ~4,6s antes de darse por vencido, con la primera espera bien corta porque la carrera
 * del morph se resuelve en el frame siguiente.
 */
const RETRY_DELAYS_MS = [400, 1200, 3000];

/**
 * Techo de espera para una petición de recomendaciones.
 *
 * REPORTADO: los recomendados "a veces no cargan" y no se recuperan hasta recargar la página.
 * Los reintentos de acá arriba NO cubrían este caso, y el motivo es que nunca llegaban a correr.
 *
 * El `AbortController` de `#fetchCachedRecommendations` sólo se dispara cuando llega un fetch
 * NUEVO que releva al anterior. Si la conexión se cuelga sin cerrar —lo normal en un teléfono que
 * pierde señal a mitad de la respuesta—, la promesa no se resuelve NI se rechaza: no se lanza
 * error, no se entra al `catch`, no se agenda ningún reintento, y el esqueleto queda a la vista
 * para siempre. Un fallo silencioso, no uno ruidoso.
 *
 * Con el techo, una petición colgada se convierte en un fallo reintentable normal y entra por el
 * mismo camino que ya existía.
 *
 * **Se aborta con un motivo propio, y eso no es decorativo**: el `catch` de `#loadRecommendations`
 * descarta los `AbortError` a propósito (son el propio componente relevando un fetch viejo, y
 * tratarlos como error escondía la sección para siempre). Abortar por tiempo con el
 * `AbortError` de fábrica caería en esa misma rama y se tragaría el fallo — o sea, el arreglo
 * no haría nada. Por eso el motivo es un `TimeoutError`, que el `catch` sí deja pasar a reintento.
 *
 * 8s es holgado contra los ~0,5s que mide el endpoint en vivo: no quiere cortar una respuesta
 * lenta, quiere ponerle un piso a una que no va a llegar nunca.
 */
const FETCH_TIMEOUT_MS = 8000;

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations.
   *
   * Se crea en `connectedCallback` y no como campo de clase porque necesita saber cuál es el
   * contenedor de scroll, y eso depende de estar en el DOM.
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * REPORTADO: los recomendados a veces no cargaban nunca y la sección quedaba en el esqueleto.
   *
   * La causa principal era que este observador NO pasaba `root`, así que medía contra la pantalla
   * — pero por spec el rect del target se recorta primero contra CADA contenedor con overflow
   * intermedio, y `rootMargin` expande sólo la raíz, nunca los recortadores. Con `.page-wrapper`
   * (escritorio), `.cart-drawer__items` y `.menu-drawer` en el medio, los 400px de precarga
   * estaban muertos y la sección cargaba recién al scrollear literalmente hasta ella.
   *
   * `getIntersectionRoot()` es el helper que el tema ya tiene para esto y que usa `header.js`:
   * devuelve `.page-wrapper` en escritorio y `null` (pantalla) en móvil. Hay que rehacerlo al
   * cruzar el breakpoint, porque el contenedor de scroll cambia.
   */
  #createIntersectionObserver = () => {
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        // `entries.some` y no `entries[0]`: el callback puede recibir varios records coalescidos,
        // y si el primero es "no intersecta" el early-return se comía el disparo y el observador
        // no volvía a avisar mientras el elemento siguiera visible. El resto del tema
        // (`product-reels.js`, `jumbo-text.js`) ya lee la última entry por este mismo motivo.
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#loadRecommendations();
      },
      { root: getIntersectionRoot(), rootMargin: '0px 0px 400px 0px' }
    );
    this.#intersectionObserver.observe(this);
  };

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // `data-recommendations-performed` no dispara nada, valga lo que valga. Horizon sólo
      // ignoraba el `'true'`, y el `'false'` lo escribe ÚNICAMENTE `#refreshRecommendations`,
      // que acto seguido llama a `#loadRecommendations` por su cuenta: el observador volvía a
      // llamarlo un microtask después, y ese segundo fetch abortaba al primero. Medido en vivo:
      // cuatro pedidos de recomendaciones por cada toque del stepper, dos de ellos abortados
      // (y el servidor los renderiza igual). También lo pisa a `'false'` el morph del cajón tras
      // cada cambio del carrito, y ese caso ya lo cubre el refresco por `cartLinesUpdate`.
      if (mutation.attributeName === 'data-recommendations-performed') continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * El pedido en vuelo, para COMPARTIRLO cuando dos disparadores piden lo mismo en el mismo
   * instante (el `toggle` del cajón de menú y su `IntersectionObserver`; el `connectedCallback`
   * del elemento recién morfeado y el refresco por `cartLinesUpdate` en cada agregado). Antes
   * el segundo abortaba al primero: medido en vivo, un pedido abortado por cada agregado y por
   * cada apertura del menú, que el servidor renderizaba igual. Sólo se comparte si arrancó
   * DESPUÉS del último cambio del carrito (`#cartChangedAt`): uno anterior podría traer
   * recomendaciones de un carrito que ya no existe, y ahí sí se aborta y se repite.
   * @type {Promise<{ success: true, data: string } | { success: false, status: number }> | null}
   */
  #activeFetchPromise = null;
  #activeFetchUrl = '';
  #activeFetchStartedAt = 0;
  #cartChangedAt = 0;

  /**
   * Cuántos fallos reintentables se acumularon desde la última carga buena. Se reinicia en cada
   * éxito y en cada refresco pedido por el carrito, así que el presupuesto de reintentos es por
   * intento de carga y no por vida del componente.
   * @type {number}
   */
  #failedAttempts = 0;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #retryTimer;

  /**
   * Hay un refresco por un cambio del carrito que todavía no se hizo.
   *
   * REPORTADO: al agregar un producto con otro ya en el carrito, el cajón lo seguía recomendando
   * hasta tocar una cantidad. Medido en vivo: el evento `cartLinesUpdate` llega, el handler agenda el
   * refresco (debounce de `RECOMMENDATIONS_REFRESH_DEBOUNCE_MS`) y, en ese intervalo, el morph del
   * carrito SACA Y VUELVE A PONER este mismo nodo (misma instancia, medido con una marca en el
   * objeto). `disconnectedCallback` cancela el debounce —correcto: no hay que refrescar un nodo
   * suelto— pero nadie lo volvía a agendar, y el `connectedCallback` no pide nada porque la bandera
   * de «ya cargado» sigue en `'true'`. Esta bandera sobrevive a la desconexión y el
   * `connectedCallback` retoma el refresco pendiente.
   */
  #refreshPending = false;

  connectedCallback() {
    super.connectedCallback();
    this.#createIntersectionObserver();
    scrollContainerMediaQuery.addEventListener('change', this.#createIntersectionObserver);
    this.#mutationObserver.observe(this, { attributes: true });
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleOpen);
    this.#detailsDrawer?.addEventListener('toggle', this.#handleDetailsToggle);

    // El estado del cajón se siembra A MANO, sincrónicamente, en vez de esperar al próximo evento
    // de apertura.
    //
    // REPORTADO como "a veces no carga" en el carrito, y la secuencia es esta: con el carrito
    // vacío este elemento NI EXISTE (el Liquid sólo lo dibuja en la rama con ítems). En el primer
    // "agregar", el cajón se abre sincrónicamente y el morph que inserta este elemento llega
    // DESPUÉS — o sea que el `DrawerOpenEvent` ya pasó y el listener de arriba queda cableado para
    // la PRÓXIMA apertura. Sin esta comprobación el único disparador vivo es el observador, y ahí
    // adentro está recortado fuera de vista: esqueleto hasta cerrar y volver a abrir.
    if (this.closest('dialog')?.open || this.#detailsDrawer?.open) {
      this.#loadRecommendations();
    }

    // Which products get recommended depends on the cart's current contents (already-in-cart
    // items are excluded) — refetch whenever a line changes, otherwise a card lingers after its
    // product has already been added (or should reappear after it's removed).
    //
    // En los TRES lugares: los dos cajones (carrito y menú) y el cierre de la ficha. Antes la
    // ficha quedaba afuera (sólo escuchaban los cajones) y recomendaba lo que ya estaba en el
    // carrito hasta recargar la página.
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);

    // Un refresco que quedó a medias porque el morph del carrito movió este nodo (ver
    // `#refreshPending`): se retoma acá.
    if (this.#refreshPending) this.#debouncedRefresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();
    scrollContainerMediaQuery.removeEventListener('change', this.#createIntersectionObserver);
    this.#mutationObserver.disconnect();
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleOpen);
    this.#detailsDrawer?.removeEventListener('toggle', this.#handleDetailsToggle);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    // A pending refresh would otherwise fire against a detached component.
    this.#debouncedRefresh.cancel();
    clearTimeout(this.#retryTimer);
  }

  /**
   * The nearest ancestor <theme-drawer>, if this instance lives inside one
   * (e.g. the cart drawer). Null otherwise.
   * @returns {Element | null}
   */
  get #themeDrawer() {
    return this.closest('theme-drawer');
  }

  /**
   * The nearest ancestor native <details>, if this instance lives inside one
   * (e.g. the category menu drawer, which — unlike the cart — opens via a
   * plain <details>/<summary> instead of <theme-drawer>, so it has no
   * DrawerOpenEvent to listen for; the native `toggle` event covers it).
   * @returns {HTMLDetailsElement | null}
   */
  get #detailsDrawer() {
    return /** @type {HTMLDetailsElement | null} */ (this.closest('details'));
  }

  /**
   * IntersectionObserver alone may not reliably notice this element becoming
   * visible when an ancestor <dialog> flips from its default display:none to
   * open (e.g. the cart drawer) — force a check on the real open event too.
   */
  #handleOpen = () => {
    this.#loadRecommendations();
  };

  #handleDetailsToggle = () => {
    if (this.#detailsDrawer?.open) this.#loadRecommendations();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.#cartChangedAt = performance.now();
        this.#refreshPending = true;
        this.#debouncedRefresh();
      })
      .catch(() => {});
  };

  /**
   * Si este componente está a la vista del cliente: dentro de un `<dialog>` abierto (el cajón
   * del carrito) o de un `<details>` abierto (el cajón de menú). Fuera de un cajón, siempre.
   * @returns {boolean}
   */
  #isShown() {
    const dialog = this.closest('dialog');
    if (dialog) return dialog.open;
    const details = this.#detailsDrawer;
    if (details) return details.open;
    return true;
  }

  /**
   * Marca los recomendados como viejos SIN pedirlos: se piden recién cuando el cajón se abra
   * (`#handleOpen` / `#handleDetailsToggle` ya llaman a `#loadRecommendations`, que sólo se
   * saltea la carga con la bandera en `'true'`). Medido en vivo: el cajón de menú, CERRADO,
   * volvía a pedir sus recomendados (29 KB, ~0,5 s) en cada toque del stepper del carrito, por
   * la misma conexión que la actualización que el cliente sí está esperando.
   */
  #markStale() {
    const { url, productId, sectionId, intent } = this.dataset;
    delete this.#cachedRecommendations[`${url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`];
    this.dataset.recommendationsPerformed = 'false';
  }

  /**
   * Collapses cart activity into ONE refresh, once the cart has actually settled.
   *
   * Debouncing alone was not enough: a single tap on a pack's stepper produces bursts of cart
   * events spread across the whole multi-step operation (remove → add → reconcile → corrective
   * update), far enough apart that each burst got its own refresh anyway. Waiting for the
   * cart's own busy flag to clear collapses the entire operation into a single fetch, and has
   * the better semantics too — recommendations depend on the cart's contents, so refreshing
   * mid-operation shows the shopper suggestions based on a half-applied cart.
   *
   * Safe against the flag ever sticking: `component-cart-items.js` force-releases it on a timer.
   */
  #debouncedRefresh = debounce(() => {
    if (document.documentElement.hasAttribute('data-cart-busy')) {
      this.#debouncedRefresh();
      return;
    }
    this.#refreshPending = false;
    // Un cajón cerrado no repinta nada que se vea: queda marcado como viejo y se pide al abrir.
    if (!this.#isShown()) {
      this.#markStale();
      return;
    }
    this.#refreshRecommendations();
  }, RECOMMENDATIONS_REFRESH_DEBOUNCE_MS);

  /**
   * Forces a fresh fetch even if recommendations already loaded once,
   * clearing the cached response for the current URL first (otherwise
   * #fetchCachedRecommendations would just replay the stale cart-state result).
   */
  #refreshRecommendations() {
    const { url, productId, sectionId, intent } = this.dataset;
    delete this.#cachedRecommendations[`${url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`];
    this.dataset.recommendationsPerformed = 'false';
    // Un refresco pedido por el carrito es un ciclo nuevo y le toca su propio presupuesto de
    // reintentos: si no, una racha de fallos vieja dejaría al primer tropiezo de este ciclo sin
    // margen y escondería la sección de entrada.
    this.#failedAttempts = 0;
    clearTimeout(this.#retryTimer);
    this.#loadRecommendations();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then(async (result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            // Un 5xx o un 429 son "volvé a intentar"; un 4xx es una respuesta definitiva sobre
            // esta petición y reintentarla sólo gastaría el presupuesto sin cambiar nada.
            this.#handleError(new Error(`Server returned ${result.status}`), {
              retryable: result.status >= 500 || result.status === 429,
            });
          }
          return;
        }

        if (result.data?.trim().length) {
          // Await morphSection (and set recommendationsPerformed only after it succeeds) rather
          // than firing-and-forgetting it. It can throw — the section wrapper it looks for by ID
          // is only ever created by a full server render of this section, so it can be
          // transiently missing if another concurrent cart-drawer re-render (e.g.
          // component-cart-items.js's own section morph, which runs on every cart change too)
          // lands in between and momentarily omits it. An un-awaited throw here would both
          // become an unhandled rejection AND, since recommendationsPerformed was already set
          // to 'true' just before, permanently block every future refresh (#loadRecommendations
          // early-returns once it's 'true', and reopening the drawer via #handleOpen doesn't
          // reset it) — leaving recommendations stuck stale/broken until a full page reload.
          await morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
          this.dataset.recommendationsPerformed = 'true';
          // Presupuesto de reintentos limpio para el próximo ciclo (el carrito puede pedir un
          // refresco en cualquier momento, y ese arranca de cero).
          this.#failedAttempts = 0;
          this.#refreshScrollHint();
        } else {
          // Reintentable: el cuerpo vacío también aparece cuando el cálculo de recomendaciones
          // todavía no está listo del lado de Shopify. Si de verdad no hay nada que recomendar,
          // los reintentos se agotan y termina escondiéndose igual que antes.
          this.#handleError(new Error('No recommendations available'), { retryable: true });
        }
      })
      .catch((e) => {
        // Un `AbortError` NO es un error: es el propio componente cancelando un fetch viejo porque
        // llegó un disparador nuevo (el observador y la apertura del cajón pueden caer en el mismo
        // frame). Tratarlo como error dejaba la sección con `hidden` + `data-error`, y de ahí no
        // se podía volver: el MutationObserver ignora deliberadamente esos dos cambios para no
        // entrar en bucle. O sea que una carrera inofensiva escondía los recomendados para
        // siempre. El fetch que ganó sigue su curso y es el que va a pintar.
        //
        // **Se compara el nombre exacto, no `instanceof DOMException` ni un `catch` atrapatodo**:
        // el techo de `FETCH_TIMEOUT_MS` aborta con un `TimeoutError`, que también es un
        // `DOMException` y también viene de un `abort()`. Ensanchar esta guarda volvería a
        // tragarse el fallo que ese techo existe para destapar, y el síntoma sería de nuevo
        // "no cargan y no se recuperan" — pero ya sin nada en la consola que lo delate.
        if (e?.name === 'AbortError') return;
        // Todo lo que llega acá es de red o del morph, y las dos cosas son transitorias por
        // naturaleza: un fallo de `fetch` (el teléfono cambiando de antena) o `morphSection`
        // sin encontrar el envoltorio de sección porque otro re-render del carrito lo pisó
        // justo en el medio.
        this.#handleError(e, { retryable: true });
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    // El mismo pedido ya en vuelo y posterior al último cambio del carrito: se comparte (ver
    // `#activeFetchPromise`). Los dos llamadores morfean el mismo HTML; el segundo morph es un
    // no-op.
    if (
      this.#activeFetch &&
      this.#activeFetchPromise &&
      this.#activeFetchUrl === url &&
      this.#activeFetchStartedAt >= this.#cartChangedAt
    ) {
      return this.#activeFetchPromise;
    }

    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;
    this.#activeFetchUrl = url;
    this.#activeFetchStartedAt = performance.now();

    const work = (async () => {
      // El temporizador cubre la descarga Y la lectura del cuerpo: se limpia recién en el `finally`,
      // que corre después del `await response.text()`. Un cuerpo que se corta a la mitad cuelga
      // igual que una conexión que nunca responde, y es el mismo síntoma.
      const timeout = setTimeout(
        () => controller.abort(new DOMException('Recommendations fetch timed out', 'TimeoutError')),
        FETCH_TIMEOUT_MS
      );

      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) {
          return { success: false, status: response.status };
        }

        const text = await response.text();
        // Una respuesta vacía NO se cachea: si se guardara, el reintento se la serviría de
        // memoria al instante y nunca volvería a pedirle nada al servidor — el reintento quedaría
        // en un gesto vacío contra la única causa que puede resolverse sola con el tiempo.
        if (text.trim().length) this.#cachedRecommendations[url] = text;
        return { success: true, data: text };
      } finally {
        clearTimeout(timeout);
        // Sólo se limpia si el controlador sigue siendo el NUESTRO. Antes se anulaba siempre, así
        // que el rechazo del fetch abortado pisaba el controlador del que lo reemplazó y un tercer
        // disparo ya no podía abortarlo: dos fetches vivos compitiendo por pintar la misma sección.
        if (this.#activeFetch === controller) {
          this.#activeFetch = null;
          this.#activeFetchPromise = null;
        }
      }
    })();
    this.#activeFetchPromise = work;
    return work;
  }

  /**
   * Every ancestor <scroll-hint> (e.g. the cart drawer nests one for its
   * overall scrollable content area, and — on mobile only — another one
   * around just the item list + recommendations), if this instance lives
   * inside any.
   * @returns {Element[]}
   */
  get #scrollHints() {
    const hints = [];
    let el = this.closest('scroll-hint');
    while (el) {
      hints.push(el);
      el = el.parentElement?.closest('scroll-hint') ?? null;
    }
    return hints;
  }

  /**
   * <scroll-hint> (assets/scrolling.js) only recomputes its fade mask on its
   * own 'scroll' event or when ITS OWN box is resized — it has no way to
   * notice that the content inside it changed height while its own box
   * stayed the same size, which is exactly what happens here: the drawer's
   * scrollable area has a fixed height (flex-grow, not content-driven), and
   * morphing the loading skeleton into the real recommendations card can
   * shrink the content from "overflowing" to "fits fine" without the
   * container itself ever resizing. Left alone, the fade mask set while the
   * (taller) skeleton was still showing lingers indefinitely, faintly
   * fading real content — reported as a stray "difuminado" near the bottom
   * of the cart drawer even when nothing is scrollable. Dispatching a
   * synthetic 'scroll' event forces scroll-hint to recompute against the
   * now-current scrollHeight/clientHeight once the morph's layout settles.
   *
   * Nudges every ancestor scroll-hint, not just the nearest one: which of
   * them actually scrolls depends on viewport width (cart-drawer.liquid's
   * .cart-drawer__items only gets its own overflow-y: auto on mobile —
   * .cart-drawer__content, one level up, is the one with real overflow on
   * desktop), so the nearest one isn't reliably the relevant one.
   */
  #refreshScrollHint() {
    requestAnimationFrame(() => {
      for (const hint of this.#scrollHints) hint.dispatchEvent(new Event('scroll'));
    });
  }

  /**
   * Handle errors in a consistent way
   *
   * Esconder la sección es el estado TERMINAL y no hay vuelta atrás: el `MutationObserver` de
   * arriba ignora deliberadamente tanto `data-error` como la clase `hidden`, así que una vez
   * escondida nada la puede recuperar. Por eso un fallo reintentable no toca ninguno de los
   * dos — se agenda otro intento y se deja el esqueleto a la vista, que es más honesto que un
   * hueco vacío mientras todavía se está intentando.
   *
   * Y por eso mismo el reintento tampoco *limpia* `hidden`: sacar esa clase dispararía el
   * `MutationObserver` (su guarda sólo saltea cuando la clase está PRESENTE), que llamaría a
   * `#loadRecommendations` por su cuenta y competiría con el reintento agendado. No agregándola
   * hasta el final, no hay nada que limpiar.
   *
   * @param {Error} error
   * @param {{ retryable?: boolean }} [options]
   */
  #handleError(error, { retryable = false } = {}) {
    if (retryable && this.#failedAttempts < RETRY_DELAYS_MS.length) {
      const delay = RETRY_DELAYS_MS[this.#failedAttempts];
      this.#failedAttempts++;

      console.warn(
        `[product-recommendations] Fallo transitorio (${error.message}). Reintento ${this.#failedAttempts}/${RETRY_DELAYS_MS.length} en ${delay}ms.`
      );

      clearTimeout(this.#retryTimer);
      this.#retryTimer = setTimeout(() => {
        // El cajón se pudo cerrar y el nodo desaparecer entre medio: un morph contra un
        // componente desconectado no pinta nada y encima gasta una request.
        if (this.isConnected) this.#loadRecommendations();
      }, delay);
      return;
    }

    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
    this.#refreshScrollHint();
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
